import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateTeamRegistrationTemplateRequest, TeamBasic, TeamRegistrationTemplate, TournamentSummary } from '../features/admin/legacyModel';
import type { AdminPlayerStats, Training } from '../features/admin/managementModel';
import { AdminDataService, type AdminAccountUser, type AdminManagedMatch } from '../services/AdminDataService';
import { PlayerDataService } from '../services/PlayerDataService';
import { TeamDataService } from '../services/TeamDataService';
import { accessQueryKeys } from './useAccessData';
import { playerQueryKeys } from './usePlayerData';
import { teamQueryKeys } from './useTeamData';
import { tournamentQueryKeys } from './tournamentQueryKeys';
import { userPeopleQueryKeys } from './useUserPeopleData';

export const adminManagementQueryKeys = {
  all: ['admin-management'] as const,
  players: () => [...adminManagementQueryKeys.all, 'players'] as const,
  trainings: () => [...adminManagementQueryKeys.all, 'trainings'] as const,
  teams: () => [...adminManagementQueryKeys.all, 'teams'] as const,
  tournaments: () => [...adminManagementQueryKeys.all, 'tournaments'] as const,
  templates: () => [...adminManagementQueryKeys.all, 'templates'] as const,
  matches: () => [...adminManagementQueryKeys.all, 'matches'] as const,
  users: () => [...adminManagementQueryKeys.all, 'users'] as const,
  playerDetail: (playerId?: string) => [...adminManagementQueryKeys.all, 'player-detail', playerId ?? 'unknown'] as const,
};

export function useAdminPlayersQuery(enabled = true) {
  return useQuery<AdminPlayerStats[]>({
    queryKey: adminManagementQueryKeys.players(),
    queryFn: AdminDataService.getAdminPlayers,
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useAdminTrainingsQuery(enabled = true) {
  return useQuery<Training[]>({
    queryKey: adminManagementQueryKeys.trainings(),
    queryFn: AdminDataService.getAdminTrainings,
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useAdminTeamsQuery(enabled = true) {
  return useQuery<TeamBasic[]>({
    queryKey: adminManagementQueryKeys.teams(),
    queryFn: AdminDataService.getAdminTeams,
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useAdminTournamentsQuery(enabled = true) {
  return useQuery<TournamentSummary[]>({
    queryKey: adminManagementQueryKeys.tournaments(),
    queryFn: AdminDataService.getAdminTournaments,
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useAdminTemplatesQuery(enabled = true) {
  return useQuery<TeamRegistrationTemplate[]>({
    queryKey: adminManagementQueryKeys.templates(),
    queryFn: AdminDataService.getAdminTemplates,
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useAdminMatchesQuery(enabled = true) {
  return useQuery<AdminManagedMatch[]>({
    queryKey: adminManagementQueryKeys.matches(),
    queryFn: AdminDataService.getAdminMatches,
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useAdminPlayerDetailQuery(playerId?: string) {
  return useQuery({
    queryKey: adminManagementQueryKeys.playerDetail(playerId),
    queryFn: () => AdminDataService.getAdminPlayerDetail(playerId!),
    enabled: Boolean(playerId),
  });
}

export function useUpdateAdminPlayerInviteEmailMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ playerId, email }: { playerId: string; email: string }) =>
      AdminDataService.updateAdminPlayerInviteEmail(playerId, email),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: adminManagementQueryKeys.playerDetail(variables.playerId) });
      queryClient.invalidateQueries({ queryKey: adminManagementQueryKeys.players() });
    },
  });
}

export function useClaimPlayerProfileInvitationsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) => AdminDataService.claimPlayerProfileInvitations(email),
    onSuccess: (_, email) => {
      queryClient.invalidateQueries({ queryKey: adminManagementQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: playerQueryKeys.management(email) });
      queryClient.invalidateQueries({ queryKey: userPeopleQueryKeys.inventory(email) });
      queryClient.invalidateQueries({ queryKey: teamQueryKeys.managed(email) });
      queryClient.invalidateQueries({ queryKey: tournamentQueryKeys.userStats(email) });
      queryClient.invalidateQueries({ queryKey: tournamentQueryKeys.userMatches('empty') });
    },
  });
}

export function useDeleteTeamMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: AdminDataService.deleteTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminManagementQueryKeys.all });
    },
  });
}

export function useDeleteAdminPlayerMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (playerId: string) => PlayerDataService.deletePlayer(playerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminManagementQueryKeys.all });
    },
  });
}

export function useDeleteTournamentMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: AdminDataService.deleteTournament,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminManagementQueryKeys.all });
    },
  });
}

export function useAdminAccountUsersQuery(enabled = true) {
  return useQuery<AdminAccountUser[]>({
    queryKey: adminManagementQueryKeys.users(),
    queryFn: AdminDataService.getAdminAccountUsers,
    enabled,
    refetchOnWindowFocus: false,
  });
}

export function useUpdateAdminAccountRolesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ email, roles }: { email: string; roles: string[] }) =>
      AdminDataService.updateAdminAccountRoles(email, roles),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminManagementQueryKeys.users() });
      queryClient.invalidateQueries({ queryKey: accessQueryKeys.current });
    },
  });
}

export function useDeleteAdHocMatchMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: AdminDataService.deleteAdHocMatch,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminManagementQueryKeys.matches() });
    },
  });
}

export function useSaveTeamRegistrationTemplateMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      payload,
    }: {
      templateId?: string;
      payload: CreateTeamRegistrationTemplateRequest;
    }): Promise<TeamRegistrationTemplate> => {
      return templateId
        ? TeamDataService.updateTeamRegistrationTemplate(templateId, payload)
        : TeamDataService.createTeamRegistrationTemplate(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminManagementQueryKeys.all });
    },
  });
}

export function useToggleTeamRegistrationTemplateStatusMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, active }: { templateId: string; active: boolean }) =>
      TeamDataService.updateTeamRegistrationTemplateStatus(templateId, active),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminManagementQueryKeys.all });
    },
  });
}
